import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import styled, { keyframes } from 'styled-components';
import * as pdfjsLib from 'pdfjs-dist';

import DropdownMenu from '../../dropdown-menu/DropdownMenu';
import MediaDetailModal from './MediaDetailModal';
import { PDFPreviewProps, MenuItem } from '../../../types/media-preview';
import { downloadMediaFile } from '../../../utils/downloadFile';

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.mjs`;

// ---- Module-scope caches (persist across mounts) ----
const CACHE_MAX = 50;
const thumbnailCache = new Map<string, string>();
const ratioCache = new Map<string, number>();
// `initialPage="last"` を再マウント時にも即解決できるようページ数を覚えておく。
const pageCountCache = new Map<string, number>();

/**
 * サムネイルキャッシュのキー。**ページ番号を含める**。
 * 同じ PDF でも描画したページによって絵が違うため、`src` だけで引くと
 * ページ送りした結果のキャッシュを別の場所のサムネイルが拾ってしまう。
 */
function thumbnailKey(src: string, pageNum: number) {
  return `${src}#p${pageNum}`;
}

function cacheThumbnail(key: string, dataUrl: string) {
  thumbnailCache.delete(key);
  if (thumbnailCache.size >= CACHE_MAX) {
    const oldest = thumbnailCache.keys().next().value;
    if (oldest !== undefined) thumbnailCache.delete(oldest);
  }
  thumbnailCache.set(key, dataUrl);
}

const A4_RATIO = 1 / 1.414;

// ---- Animations ----
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.6; }
  50% { opacity: 0.3; }
`;

// ---- Styled Components (canvas mode) ----
const Container = styled.div<{ $fixedHeight?: boolean }>`
  position: relative;
  width: 100%;
  height: ${props => props.$fixedHeight ? '100%' : 'auto'};
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  border: 1px solid var(--soremo-border);
  border-radius: 6px;
  animation: ${fadeIn} 0.3s ease-in-out;
`;

const CanvasWrapper = styled.div<{ $fixedHeight?: boolean }>`
  position: relative;
  width: 100%;
  height: ${props => props.$fixedHeight ? '100%' : 'auto'};
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Canvas = styled.canvas`
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  display: block;
`;

const CachedImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  display: block;
`;

// ---- Styled Components (native viewer mode) ----
const NativeViewerWrapper = styled.div`
  position: relative;
  width: 100%;
  max-height: 100dvh;
  border-radius: 6px;
  overflow: hidden;
`;

const StyledEmbed = styled.embed`
  width: 100%;
  height: 100%;
  border: none;
  display: block;
`;

const OutsideControlsOverlay = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 4px 0 0;
`;

// ---- Styled Components (shared) ----
const SkeletonBox = styled.div`
  background: rgba(128, 128, 128, 0.15);
  border-radius: 6px;
  display: flex;
  justify-content: center;
  align-items: center;
  animation: ${pulse} 1.5s ease-in-out infinite;

  .material-symbols-rounded {
    font-size: 32px;
    color: rgba(128, 128, 128, 0.4);
  }
`;

const Controls = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  padding: 10px;
  background: rgba(0, 0, 0, 0.5);
  opacity: 0;
  transition: opacity 0.3s;

  ${Container}:hover & {
    opacity: 1;
  }
`;

const PageInfo = styled.div`
  color: white;
  font-size: 14px;
`;

const NavigationButton = styled.button`
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 0 10px;
  font-size: 16px;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: #ff6b6b;
  text-align: center;
  padding: 20px;
  background: rgba(255, 107, 107, 0.1);
  border-radius: 6px;
  margin: 10px;
`;

// ---- Component ----
const PDFPreview: React.FC<PDFPreviewProps> = ({
  src,
  thumbnailSrc = null,
  sizeMode = { mode: 'auto', objectFit: 'contain' },
  showMenu = false,
  showControls = false,
  onLoaded = null,
  menuOutside = false,
  onDownload,
  downloadUrl,
  fileName,
  fileSize,
  disableInteraction = false,
  initialPage = 1,
}) => {
  // disableInteraction=true → canvas 描画（メッセージ一覧など自動読み込みでダウンロードを防ぐ）
  // disableInteraction=false → Blob URL + <embed> 表示（モーダルなどユーザー操作で開く場面）
  const useCanvasMode = sizeMode.mode === 'fixed-height' || disableInteraction;
  const isThumbnailMode = sizeMode.mode === 'fixed-height';

  // サーバー生成サムネイル（Lambda が PDF 1 ページ目から生成した画像）があれば
  // pdf.js での PDF 全体 fetch + レンダリングをスキップして <img> 表示する。
  // ページ送り（showControls）が必要な場面では PDF 本体が必要なので使わない。
  const [serverThumbFailed, setServerThumbFailed] = useState(false);
  const serverThumbnailUrl = useCanvasMode && !showControls && !serverThumbFailed
    ? thumbnailSrc || undefined
    : undefined;

  // 描画対象ページ。`"last"` は PDF を開くまで確定しないが、一度読んだ PDF は
  // ページ数をキャッシュしているので再マウント時は即座に解決できる。
  const targetPage =
    initialPage === "last"
      ? (src ? pageCountCache.get(src) : undefined)
      : initialPage;

  const cachedDataUrl =
    serverThumbnailUrl ||
    (src && targetPage ? thumbnailCache.get(thumbnailKey(src, targetPage)) : undefined);

  const fixedHeight = sizeMode.mode === 'fixed-height' || sizeMode.mode === 'fill';
  const height = sizeMode.mode === 'fixed-height' && sizeMode.height ? parseFloat(sizeMode.height) : null;
  const maxWidth = sizeMode.mode === 'fixed-height' ? sizeMode.maxWidth : (sizeMode.mode === 'auto' ? sizeMode.maxWidth : null);
  const maxHeight = sizeMode.mode === 'fixed-width' ? sizeMode.maxHeight : (sizeMode.mode === 'auto' ? sizeMode.maxHeight : null);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [pdfDoc, setPdfDoc] = useState<pdfjsLib.PDFDocumentProxy | null>(null);
  const [currentPage, setCurrentPage] = useState(targetPage ?? 1);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pageAspectRatio, setPageAspectRatio] = useState(
    () => (src ? ratioCache.get(src) : undefined) ?? A4_RATIO
  );
  const [showDetailModal, setShowDetailModal] = useState(false);

  const loadingTaskRef = useRef<pdfjsLib.PDFDocumentLoadingTask | null>(null);
  const renderTaskRef = useRef<pdfjsLib.RenderTask | null>(null);
  const renderRequestIdRef = useRef(0);

  // ---- Native mode: fetch → Blob URL（Content-Disposition: attachment を回避） ----
  const [embedBlobUrl, setEmbedBlobUrl] = useState<string | null>(null);

  useEffect(() => {
    if (useCanvasMode || !src) return;

    let revoked = false;

    const load = async () => {
      try {
        const res = await fetch(src);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const buf = await res.arrayBuffer();
        if (revoked) return;
        const blob = new Blob([buf], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        setEmbedBlobUrl(url);
        onLoaded?.();
      } catch (err) {
        if (revoked) return;
        console.warn('[PDFPreview] Blob fetch failed:', err);
        setError(err instanceof Error ? err.message : 'Failed to load PDF');
        onLoaded?.();
      }
    };
    load();

    return () => {
      revoked = true;
      setEmbedBlobUrl(prev => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src, useCanvasMode]);

  // ---- Canvas mode: render a single page to canvas ----
  const renderPage = useCallback(async (pdf: pdfjsLib.PDFDocumentProxy, pageNum: number) => {
    if (!canvasRef.current) return;

    const requestId = ++renderRequestIdRef.current;

    if (renderTaskRef.current) {
      try { renderTaskRef.current.cancel(); } catch { /* ignore */ }
      renderTaskRef.current = null;
    }

    let page: pdfjsLib.PDFPageProxy | null = null;

    try {
      page = await pdf.getPage(pageNum);

      if (requestId !== renderRequestIdRef.current) {
        try { page.cleanup(); } catch { /* ignore */ }
        return;
      }

      const canvas = canvasRef.current;
      if (!canvas) return;

      const context = canvas.getContext('2d');
      if (!context) throw new Error('Failed to get canvas context');

      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const viewport = page.getViewport({ scale: 1.0 });
      let scale = 1.0;

      if (fixedHeight && height) {
        scale = height / viewport.height;
      } else {
        const containerWidth = containerRef.current?.clientWidth;
        const targetWidth = containerWidth && containerWidth > 0 ? containerWidth : 800;
        scale = targetWidth / viewport.width;
      }

      if (maxWidth && !maxWidth.includes('%')) {
        const maxWidthPx = parseFloat(maxWidth);
        if (!isNaN(maxWidthPx) && viewport.width * scale > maxWidthPx) {
          scale = maxWidthPx / viewport.width;
        }
      }

      const displayViewport = page.getViewport({ scale });
      const renderViewport = page.getViewport({ scale: scale * dpr });

      canvas.width = Math.floor(renderViewport.width);
      canvas.height = Math.floor(renderViewport.height);
      canvas.style.width = `${Math.floor(displayViewport.width)}px`;
      canvas.style.height = `${Math.floor(displayViewport.height)}px`;

      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = 'high';

      const task = page.render({
        canvasContext: context,
        viewport: renderViewport,
      });

      renderTaskRef.current = task;
      await task.promise;

      if (requestId === renderRequestIdRef.current) {
        renderTaskRef.current = null;

        if (src) {
          try {
            const dataUrl = canvas.toDataURL('image/webp', 0.8);
            cacheThumbnail(thumbnailKey(src, pageNum), dataUrl);
          } catch { /* ignore */ }
        }
      }
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'name' in err && err.name === 'RenderingCancelledException') {
        return;
      }
      console.error('Error rendering PDF page:', err);
      throw err;
    } finally {
      if (page && requestId === renderRequestIdRef.current) {
        try { page.cleanup(); } catch { /* ignore */ }
      }
    }
  }, [fixedHeight, height, maxWidth, src]);

  // ---- Load PDF ----
  useEffect(() => {
    // Canvas mode でキャッシュがあればスキップ
    if (useCanvasMode && cachedDataUrl) {
      setLoading(false);
      onLoaded?.();
      return;
    }

    // ネイティブモードでは pdfjs 不要（Blob URL 取得に任せる）
    if (!useCanvasMode) {
      if (src && ratioCache.has(src)) {
        setPageAspectRatio(ratioCache.get(src)!);
      }
      setLoading(false);
      return;
    }

    let cancelled = false;

    const loadPDF = async () => {
      try {
        setLoading(true);
        setError(null);

        if (renderTaskRef.current) {
          try { renderTaskRef.current.cancel(); } catch { /* ignore */ }
          renderTaskRef.current = null;
        }

        if (loadingTaskRef.current) {
          try { loadingTaskRef.current.destroy(); } catch { /* ignore */ }
          loadingTaskRef.current = null;
        }

        if (pdfDoc) {
          try { await pdfDoc.destroy(); } catch { /* ignore */ }
        }

        if (!src) throw new Error('PDF source is not provided');

        const task = pdfjsLib.getDocument({
          url: src,
          disableAutoFetch: true,
        });

        loadingTaskRef.current = task;
        const pdf = await task.promise;

        if (cancelled) {
          try { await pdf.destroy(); } catch { /* ignore */ }
          return;
        }

        // Detect real page dimensions for aspect ratio
        try {
          const firstPage = await pdf.getPage(1);
          const vp = firstPage.getViewport({ scale: 1.0 });
          const ratio = vp.width / vp.height;
          ratioCache.set(src, ratio);
          if (!cancelled) setPageAspectRatio(ratio);
          firstPage.cleanup();
        } catch { /* keep default A4 ratio */ }

        if (cancelled) {
          try { await pdf.destroy(); } catch { /* ignore */ }
          return;
        }

        setPdfDoc(pdf);
        setTotalPages(pdf.numPages);
        pageCountCache.set(src, pdf.numPages);
        setCurrentPage(
          initialPage === 'last'
            ? pdf.numPages
            : Math.min(Math.max(initialPage, 1), pdf.numPages),
        );
        setLoading(false);
        onLoaded?.();
      } catch (err) {
        if (cancelled) return;
        console.error('Error loading PDF:', err);
        setError(err instanceof Error ? err.message : 'Unknown error occurred');
        setLoading(false);
        onLoaded?.();
      }
    };

    if (src) loadPDF();

    return () => {
      cancelled = true;
      if (renderTaskRef.current) {
        try { renderTaskRef.current.cancel(); } catch { /* ignore */ }
        renderTaskRef.current = null;
      }
      if (loadingTaskRef.current) {
        try { loadingTaskRef.current.destroy(); } catch { /* ignore */ }
        loadingTaskRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src, serverThumbFailed]);

  // ---- Render page to canvas (canvas mode only) ----
  useEffect(() => {
    if (!useCanvasMode || cachedDataUrl || !pdfDoc || loading) return;

    renderPage(pdfDoc, currentPage).catch(err => {
      console.error('Error rendering PDF page in effect:', err);
      setError(err instanceof Error ? err.message : 'Failed to render page');
    });
  }, [pdfDoc, loading, currentPage, renderPage, useCanvasMode, cachedDataUrl]);

  // ---- Page navigation ----
  const changePage = useCallback((newPage: number) => {
    if (newPage < 1 || newPage > totalPages) return;
    setCurrentPage(newPage);
  }, [totalPages]);

  // ---- Menu actions ----
  const handleDownload = useCallback(() => {
    if (!downloadMediaFile({ downloadUrl, src, fileName, fallbackName: 'document.pdf' })) return;
    onDownload?.();
  }, [src, downloadUrl, onDownload, fileName]);

  const handleDetailClick = useCallback(() => {
    setShowDetailModal(true);
  }, []);

  const menuItems: MenuItem[] = useMemo(() => [
    { label: '詳細情報', onClick: handleDetailClick },
    { label: 'ダウンロード', onClick: handleDownload },
  ], [handleDetailClick, handleDownload]);

  // ---- Skeleton style based on sizeMode + detected pageAspectRatio ----
  const skeletonStyle = useMemo((): React.CSSProperties => {
    switch (sizeMode.mode) {
      case 'fixed-height': {
        const h = height || 192;
        return {
          width: `${Math.round(h * pageAspectRatio)}px`,
          height: `${h}px`,
        };
      }
      case 'fixed-width':
        return {
          width: sizeMode.width || '100%',
          aspectRatio: `${pageAspectRatio}`,
          ...(maxHeight ? { maxHeight } : {}),
        };
      case 'fill':
        return { width: '100%', height: '100%' };
      case 'auto':
      default:
        return {
          width: '100%',
          aspectRatio: `${pageAspectRatio}`,
          ...(maxWidth ? { maxWidth } : {}),
          ...(maxHeight ? { maxHeight } : {}),
        };
    }
  }, [sizeMode, height, maxWidth, maxHeight, pageAspectRatio]);

  // ---- Canvas mode container style (for disableInteraction + non-fixed-height) ----
  const canvasContainerStyle = useMemo((): React.CSSProperties => {
    if (isThumbnailMode) return {};

    switch (sizeMode.mode) {
      case 'fixed-width':
        return {
          width: sizeMode.width || '100%',
          ...(maxHeight ? { maxHeight } : {}),
          overflow: 'hidden',
        };
      case 'fill':
        return { width: '100%', height: '100%', overflow: 'hidden' };
      case 'auto':
      default:
        return {
          width: '100%',
          ...(sizeMode.maxWidth ? { maxWidth: sizeMode.maxWidth } : {}),
          ...(sizeMode.maxHeight ? { maxHeight: sizeMode.maxHeight } : {}),
          overflow: 'hidden',
        };
    }
  }, [isThumbnailMode, sizeMode, maxHeight]);

  // ---- Native viewer container style ----
  const nativeViewerStyle = useMemo((): React.CSSProperties => {
    if (useCanvasMode) return {};

    const ratio = `${pageAspectRatio}`;

    switch (sizeMode.mode) {
      case 'fixed-width': {
        const maxH = sizeMode.maxHeight;
        const hasAbsoluteMaxH = maxH && !maxH.endsWith('%');
        return {
          width: sizeMode.width || '100%',
          aspectRatio: ratio,
          ...(hasAbsoluteMaxH ? { maxHeight: maxH } : {}),
        };
      }
      case 'fill':
        return { width: '100%', height: '100%', maxHeight: '100%' };
      case 'auto':
      default: {
        const maxH = sizeMode.maxHeight;
        const hasAbsoluteMaxH = maxH && !maxH.endsWith('%');
        return {
          width: '100%',
          aspectRatio: ratio,
          ...(hasAbsoluteMaxH ? { maxHeight: maxH } : {}),
          maxWidth: sizeMode.maxWidth,
        };
      }
    }
  }, [sizeMode, useCanvasMode, pageAspectRatio]);

  const renderMenu = () => {
    if (!showMenu) return null;
    if (menuOutside) {
      return (
        <OutsideControlsOverlay onClick={(e) => e.stopPropagation()}>
          <DropdownMenu items={menuItems} />
        </OutsideControlsOverlay>
      );
    }
    return <DropdownMenu items={menuItems} />;
  };

  const renderDetailModal = () => {
    if (!showDetailModal) return null;
    return (
      <MediaDetailModal
        onClose={() => setShowDetailModal(false)}
        fileName={fileName}
        fileSize={fileSize}
      />
    );
  };

  // ======== Render ========

  if (error) {
    return (
      <Container $fixedHeight={fixedHeight}>
        <ErrorMessage>
          PDFの読み込みに失敗しました: {error}
        </ErrorMessage>
      </Container>
    );
  }

  // ---- Loading skeleton ----
  if (loading || (!useCanvasMode && !embedBlobUrl && !error)) {
    return (
      <SkeletonBox style={skeletonStyle}>
        <span className="material-symbols-rounded">picture_as_pdf</span>
      </SkeletonBox>
    );
  }

  // ---- Native viewer (モーダルなど disableInteraction=false の場面) ----
  if (!useCanvasMode && embedBlobUrl) {
    return (
      <div style={{ width: '100%' }}>
        <NativeViewerWrapper style={nativeViewerStyle}>
          <StyledEmbed
            src={`${embedBlobUrl}#toolbar=0&navpanes=0&scrollbar=0`}
            type="application/pdf"
          />
        </NativeViewerWrapper>
        {renderMenu()}
        {renderDetailModal()}
      </div>
    );
  }

  // ---- Canvas mode: cached image / server-generated thumbnail ----
  if (cachedDataUrl) {
    return (
      <div style={canvasContainerStyle}>
        <Container ref={containerRef} $fixedHeight={fixedHeight}>
          <CanvasWrapper $fixedHeight={fixedHeight}>
            <CachedImage
              src={cachedDataUrl}
              alt=""
              loading="lazy"
              style={isThumbnailMode && height ? { height: `${height}px` } : { width: '100%', height: 'auto' }}
              onError={
                serverThumbnailUrl && cachedDataUrl === serverThumbnailUrl
                  ? () => setServerThumbFailed(true)
                  : undefined
              }
            />
          </CanvasWrapper>
        </Container>
        {renderMenu()}
        {renderDetailModal()}
      </div>
    );
  }

  // ---- Canvas mode: live render ----
  return (
    <div style={canvasContainerStyle}>
      <Container ref={containerRef} $fixedHeight={fixedHeight}>
        <CanvasWrapper $fixedHeight={fixedHeight}>
          <Canvas ref={canvasRef} />
        </CanvasWrapper>
        {showControls && totalPages > 1 && (
          <Controls>
            <NavigationButton
              onClick={() => changePage(currentPage - 1)}
              disabled={currentPage === 1}
            >
              ←
            </NavigationButton>
            <PageInfo>
              {currentPage} / {totalPages}
            </PageInfo>
            <NavigationButton
              onClick={() => changePage(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              →
            </NavigationButton>
          </Controls>
        )}
      </Container>
      {renderMenu()}
      {renderDetailModal()}
    </div>
  );
};

export default PDFPreview;
